<script setup lang="ts">
import type { IPagination } from "@/types"
import type { InformationItem } from "@/types/informationItem.interface"
import { computed } from "vue"
import { Button, Card, Expandable, Icon, NoData, Pagination, ReadMore } from "@/components/ui"
import { useTranslation } from "@/composables"

interface EventAdditionalInformationItemsProps {
  items: InformationItem[]
  title?: string
  description?: string
  emptyHeadline?: string
  emptyContent?: string
  pagination?: Partial<IPagination>
  showPagination?: boolean
  collapsible?: boolean
  initialOpen?: boolean
}

const props = withDefaults(defineProps<EventAdditionalInformationItemsProps>(), {
  showPagination: false,
  collapsible: false,
  initialOpen: false,
})

const emit = defineEmits<{
  paginationPrevious: [current: number]
  paginationNext: [current: number]
}>()

const { t } = useTranslation()

const sortedItems = computed(() => {
  return [...props.items].sort((itemA, itemB) => itemA.position - itemB.position)
})

const emptyHeadline = computed(() => props.emptyHeadline ?? t("event_additional_information_items.empty_headline"))
const emptyContent = computed(() => props.emptyContent ?? t("event_additional_information_items.empty_content"))

function onPaginationPrevious(current: number) {
  emit("paginationPrevious", current)
}

function onPaginationNext(current: number) {
  emit("paginationNext", current)
}
</script>

<template>
  <section class="w-full py-4 md:py-6" data-test="event-additional-information-items">
    <Card :with-padding="false" variant="subtle" border-radius="2xl" class="overflow-hidden">
      <Expandable :initial-open="collapsible ? initialOpen : true">
        <template #activator="{ toggle, isOpen, activatorAttrs }">
          <component
            :is="collapsible ? 'button' : 'div'"
            v-bind="collapsible ? activatorAttrs : {}"
            class="flex w-full items-center justify-between gap-3 p-4 text-left md:p-5"
            :class="{ 'border-b border-primary/10': isOpen, 'cursor-pointer': collapsible }"
            data-test="event-additional-information-items-header"
            @click="collapsible ? toggle() : undefined"
          >
            <div class="min-w-0">
              <h2 v-if="title" class="font-heading text-lg font-semibold tracking-tight text-primary md:text-xl" data-test="event-additional-information-items-heading">
                {{ title }}
              </h2>
              <p v-if="description" class="mt-1 max-w-2xl font-body text-sm leading-relaxed text-primary/70" data-test="event-additional-information-items-description">
                {{ description }}
              </p>
            </div>
            <Icon v-if="collapsible" icon="CircleChevronDown" class="shrink-0 transition-transform duration-200 ease-in-out" :class="{ 'rotate-180': isOpen }" />
          </component>
        </template>

        <NoData
          v-if="!sortedItems.length"
          :headline="emptyHeadline"
          :content="emptyContent"
          hide-button
          class="px-4"
          data-test="event-additional-information-items-empty"
        />

        <ul v-else class="divide-y divide-primary/10" data-test="event-additional-information-items-list">
          <li
            v-for="item in sortedItems"
            :key="item.id"
            class="min-w-0"
            data-test="event-additional-information-item"
            :data-test-item-id="item.id"
            :data-test-item-type="item.type"
          >
            <div v-if="item.type === 'text'" class="p-4 md:p-5">
              <h3 class="mb-1 font-heading text-base font-semibold leading-snug text-primary" data-test="event-additional-information-item-title">
                {{ item.title }}
              </h3>
              <ReadMore
                :content="item.value"
                :lines="2"
                data-test="event-additional-information-item-text"
              />
            </div>

            <div
              v-else
              class="p-4 md:p-5"
            >
              <div class="min-w-0 md:flex md:items-center md:justify-between md:gap-6">
                <div class="min-w-0">
                  <h3 class="font-heading text-base font-semibold leading-snug text-primary" data-test="event-additional-information-item-title">
                    {{ item.title }}
                  </h3>
                  <a
                    :href="item.value"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="mt-1 block truncate font-body text-sm text-link hover:text-link-hover hover:underline focus:text-link-hover focus:underline"
                  >
                    {{ item.value }}
                  </a>
                </div>
                <Button
                  :href="item.value"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="tertiary"
                  shape="pill"
                  size="sm"
                  suffix-icon="MoveUpRight"
                  class="mt-3 w-fit md:mt-0"
                  :data-test="item.type === 'document' ? 'event-additional-information-item-document' : 'event-additional-information-item-link'"
                >
                  {{ t("view") }}
                </Button>
              </div>
            </div>
          </li>
        </ul>

        <div v-if="showPagination && sortedItems.length" class="border-t border-primary/10 py-3 px-3">
          <Pagination
            :current-page="pagination?.page?.value"
            :page-count="pagination?.pageCount?.value"
            :can-next="pagination?.canNext?.value"
            :can-previous="pagination?.canPrevious?.value"
            @previous="onPaginationPrevious"
            @next="onPaginationNext"
          />
        </div>
      </Expandable>
    </Card>
  </section>
</template>
